import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { AlarmClock, OctagonAlert } from "lucide-react";
import { api, errMessage } from "@/lib/api";
import { fmtTime } from "@/lib/time";
import { ReviseButton } from "@/components/take5/Dialogs";
import { CompleteButton } from "@/components/take5/CompleteButton";

const SOON_MIN = 60;

function fmtDelta(min) {
  if (min < 60) return `${min} min`;
  return `${Math.floor(min / 60)} h ${String(min % 60).padStart(2, "0")}`;
}

const Reminder = ({ t, now, onChange }) => {
  const navigate = useNavigate();
  const diff = Math.round((new Date(t.planned_end_at).getTime() - now) / 60000);
  const overdue = diff <= 0;
  const revise = async (reason) => {
    try {
      const r = await api.post(`/take5s/${t.id}/revise`, { reason });
      toast.success("Nouvelle révision créée.");
      navigate(`/take5/${r.data.id}`);
      return null;
    } catch (e) { return errMessage(e); }
  };
  return (
    <div data-testid={`end-reminder-${t.id}`} role={overdue ? "alert" : "status"}
      className={`rounded-xl border-2 p-4 ${overdue ? "border-red-500 bg-red-950/60" : "border-amber-500 bg-amber-950/50"}`}>
      <div className="flex items-start gap-3">
        {overdue ? <OctagonAlert className="mt-0.5 h-5 w-5 shrink-0 text-red-300" /> : <AlarmClock className="mt-0.5 h-5 w-5 shrink-0 text-amber-300" />}
        <div className="min-w-0 flex-1">
          <Link to={`/take5/${t.id}`} className="block truncate font-semibold hover:underline">{t.task}</Link>
          <p data-testid={`end-reminder-${t.id}-text`} className={`text-sm ${overdue ? "text-red-200" : "text-amber-200"}`}>
            {overdue
              ? `Fin prévue dépassée depuis ${fmtDelta(-diff)} (${fmtTime(t.planned_end_at, t.site)}) : réévaluation nécessaire avant de poursuivre.`
              : `Fin prévue à ${fmtTime(t.planned_end_at, t.site)} : il reste ${fmtDelta(diff)}.`}
          </p>
        </div>
      </div>
      <div className="mt-3 flex flex-wrap gap-2"><ReviseButton onRevise={revise} /><CompleteButton id={t.id} testId={`end-reminder-${t.id}-complete`} onDone={() => onChange?.()} /></div>
    </div>
  );
};

export const EndReminders = ({ take5s, onChange }) => {
  const [now, setNow] = useState(Date.now());
  useEffect(() => {
    const id = setInterval(() => setNow(Date.now()), 30000);
    return () => clearInterval(id);
  }, []);
  const due = take5s.filter((t) => t.status === "autorisee" && !t.superseded_by && t.planned_end_at
    && new Date(t.planned_end_at).getTime() - now <= SOON_MIN * 60000)
    .sort((a, b) => new Date(a.planned_end_at) - new Date(b.planned_end_at));
  if (!due.length) return null;
  return (
    <div data-testid="end-reminders" className="mb-6 space-y-3">
      {due.slice(0, 3).map((t) => <Reminder key={t.id} t={t} now={now} onChange={onChange} />)}
      {due.length > 3 && <Link to="/activites" data-testid="end-reminders-more" className="block text-sm font-semibold text-amber-400">{due.length - 3} autre(s) Take 5 à surveiller →</Link>}
    </div>
  );
};
